import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { clearCustomerAccountCache } from '../api/clearCustomerAccountCache.js';
import { clearAllRequestFailures } from '../api/requestFailureCache.js';
import { customerAuthBus } from '../crossTab/customerCrossTab.js';
import { useTenantCode } from '../tenant/TenantProvider.js';
import { useCustomerSession } from './CustomerSessionProvider.js';

/** Drop cached account rows when the signed-in customer changes (e.g. phone still had an old refresh cookie). */
export function CustomerAccountCacheSync(): null {
  const queryClient = useQueryClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const previousCustomerId = useRef<number | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = customerAuthBus.subscribe((message) => {
      if (message.type === 'login' || message.type === 'logout') {
        // Mirror admin AdminCacheSync: drop poisoned GET failure TTL so post-login
        // preference sync (receipt-locales) is not blocked by a pre-auth 401 race.
        clearAllRequestFailures();
        clearCustomerAccountCache(queryClient, tenantCode);
      }
    });
    return unsubscribe;
  }, [queryClient, tenantCode]);

  useEffect(() => {
    const customerId = session?.customerId;
    const previous = previousCustomerId.current;

    if (customerId === undefined) {
      if (previous !== undefined) {
        clearCustomerAccountCache(queryClient, tenantCode);
      }
      previousCustomerId.current = undefined;
      return;
    }

    if (previous !== undefined && customerId !== previous) {
      clearCustomerAccountCache(queryClient, tenantCode);
    }
    // Same-tab establishSession (login) — clear failure cache even when customerId
    // is first set (auth bus may be cross-tab only for some paths).
    if (previous === undefined) {
      clearAllRequestFailures();
    }
    previousCustomerId.current = customerId;
  }, [queryClient, session?.customerId, tenantCode]);

  return null;
}
